import React from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';
import moment from 'moment-timezone';
import cx from 'classnames';

import Quote from 'models/quote';
import { Location } from 'models/locations';
import Search from 'models/search';

import OptimizedDateTimeSelector from 'components/checkout/date-time-selector';
import AutoExtendLabel from 'components/checkout/parking-details/time-picker/auto-extend-label';
import SelectTimeToolTip from 'components/common/select-time-tooltip';

const convertTime = (time, timezone) => (moment(time).tz(timezone));

const propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  directAffiliateTraffic: PropTypes.bool.isRequired,
  shouldRenderAutoExtendLabel: PropTypes.bool.isRequired,
  bookExtendedTimes: PropTypes.bool.isRequired,
  timezone: PropTypes.string.isRequired,
  error: PropTypes.instanceOf(Map).isRequired,
  editTimeLabel: PropTypes.string.isRequired,
  appContext: PropTypes.string.isRequired,
  timePickerPromptDismissed: PropTypes.bool.isRequired,
  showAutoExtendTooltip: PropTypes.bool.isRequired,
  selectedQuote: PropTypes.instanceOf(Quote),
  selectedLocation: PropTypes.instanceOf(Location),
  sellers: PropTypes.instanceOf(Map),
  toggleBookExtendedTimes: PropTypes.func.isRequired,
  toggleAutoExtendTooltip: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  startTimeChange: PropTypes.func.isRequired,
  startDateChange: PropTypes.func.isRequired,
  endTimeChange: PropTypes.func.isRequired,
  endDateChange: PropTypes.func.isRequired,
  showMobileTimePicker: PropTypes.func.isRequired,
  onEditTimeVisibilityChange: PropTypes.func.isRequired,
  hideAutoExtendTooltip: PropTypes.func.isRequired,
  dismissTimePickerPrompt: PropTypes.func.isRequired,
};

const defaultProps = {
  selectedQuote: new Quote(),
  selectedLocation: new Location(),
  sellers: Map(),
  position: 'bottom',
};

const CheckoutTimePicker = ({
  timezone,
  trackEvent,
  currentSearch,
  selectedQuote,
  selectedLocation,
  error,
  bookExtendedTimes,
  startTimeChange,
  startDateChange,
  endTimeChange,
  endDateChange,
  showMobileTimePicker,
  onEditTimeVisibilityChange,
  shouldRenderAutoExtendLabel,
  directAffiliateTraffic,
  sellers,
  toggleBookExtendedTimes,
  toggleAutoExtendTooltip,
  editTimeLabel,
  appContext,
  timePickerPromptDismissed,
  dismissTimePickerPrompt,
  showAutoExtendTooltip,
  hideAutoExtendTooltip,
}) => {
  if (!(selectedQuote.isTransient || selectedQuote.isAirport)) { return null; }

  const effectiveEndTime = bookExtendedTimes ? selectedQuote.maxEndTime : selectedQuote.endTime;
  const startTime = convertTime(selectedQuote.startTime, timezone);
  const endTime = convertTime(effectiveEndTime, timezone);

  const classNames = cx({
    'row': true,
    'noselect': true,
    'margin-top-xs-30': true,
    'margin-bottom-10': true,
    'martin-top-sm-0': shouldRenderAutoExtendLabel,
  });

  return (
    <div className="margin-bottom-20">
      <div className={classNames}>
        <div className="col-xs-12">
          <div className="border-style-solid border-side-all border-color-light-grey padding-20">
            <div onClick={showMobileTimePicker}>
              <AutoExtendLabel
                position="top"
                shouldRender={shouldRenderAutoExtendLabel}
                directAffiliateTraffic={directAffiliateTraffic}
                bookExtendedTimes={bookExtendedTimes}
                timezone={timezone}
                selectedQuote={selectedQuote}
                selectedLocation={selectedLocation}
                sellers={sellers}
                toggleBookExtendedTimes={toggleBookExtendedTimes}
                toggleAutoExtendTooltip={toggleAutoExtendTooltip}
                appContext={appContext}
                showAutoExtendTooltip={showAutoExtendTooltip}
                hideAutoExtendTooltip={hideAutoExtendTooltip}
              />
              <div className="row">
                <div className="col-sm-5 margin-bottom-xs-10 margin-bottom-sm-0 checkout-start-time-picker clickable">
                  <OptimizedDateTimeSelector
                    timezone={timezone}
                    dateChange={startDateChange}
                    timeChange={startTimeChange}
                    field="startTime"
                    label="Start"
                    time={startTime}
                    trackEvent={trackEvent}
                    currentSearch={currentSearch}
                    hideOnDateChange={false}
                    checkoutError={error}
                    displayTimes
                  />
                </div>
                <div className="col-sm-2 hidden-xs text-align-center padding-top-20">
                  <span className="iconified-font iconified-extra-thin-arrow-right text-color-grey" />
                </div>
                <div className="col-sm-5 checkout-end-time-picker clickable">
                  <OptimizedDateTimeSelector
                    timezone={timezone}
                    dateChange={endDateChange}
                    timeChange={endTimeChange}
                    field="endTime"
                    label="End"
                    time={endTime}
                    trackEvent={trackEvent}
                    currentSearch={currentSearch}
                    hideOnDateChange={false}
                    checkoutError={error}
                    displayTimes
                  />
                </div>
              </div>
            </div>
            <div className="checkout-select-time-tool-tip-optimized">
              <SelectTimeToolTip
                dismissTimePickerPrompt={dismissTimePickerPrompt}
                timePickerPromptDismissed={timePickerPromptDismissed}
                trackEvent={trackEvent}
                currentSearch={currentSearch}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="text-color-blue text-weight-medium text-align-center padding-vertical-5 text-size-12 margin-bottom-10 visible-xs"
        onClick={showMobileTimePicker}
      >
        <VisibilitySensor onChange={onEditTimeVisibilityChange} partialVisibility>
          <span>
            + {editTimeLabel}
          </span>
        </VisibilitySensor>
      </div>
    </div>
  );
};

CheckoutTimePicker.propTypes = propTypes;
CheckoutTimePicker.defaultProps = defaultProps;
export default CheckoutTimePicker;
